import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import subfeeDeleteApi from 'models/productManagement/subfee/delete/Api.Subfee.Delete';
import Subfee from 'models/productManagement/subfee/Model.Subfee';
import { useMemo } from 'react';
import { subfeeIndexRoute } from './Subfee.Index';

export enum SubfeeAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifySubfeeAction = Utils.VerifyExtends<
  Module.ProductManagement.Subfee.Actions,
  Utils.ValueOf<typeof SubfeeAction>
>;
export default function useSubfee() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<Module.ProductManagement.Subfee.Params, Module.ProductManagement.Subfee.Query>({
    route: subfeeIndexRoute,
  });
  const canView = permissions.CREATE_SUBFEE || permissions.EDIT_SUBFEE || permissions.DELETE_SUBFEE;
  const listApi = useApi({
    action: Subfee.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
    wait: !canView,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const deleteApi = useApi({
    action: subfeeDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  return {
    canView,
    pageRouter,
    listApi,
    deleteApi,
    selected,
    permissions,
    views: {
      [SubfeeAction.DELETE]: pageRouter.params.action === SubfeeAction.DELETE && selected && permissions.DELETE_SUBFEE,
      [SubfeeAction.EDIT]:
        pageRouter.params.action === SubfeeAction.EDIT &&
        ((permissions.CREATE_SUBFEE && !selected) || (permissions.EDIT_SUBFEE && selected)),
    },
  };
}

export function useSubfeeProvider() {
  return useDataProvider<ReturnType<typeof useSubfee>>();
}
